import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import { Card, CardActionArea, CardContent, Checkbox, TextField, Theme, Tooltip, Typography, useTheme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { CopyableMealPlanFragment } from "apps/web/src/types";
import React, { Dispatch } from "react";
import { MealPlanCopyOptionsFormAction, MealPlanCopyOptionsFormState } from "./utils";

const useStyles = makeStyles(({ spacing, palette: { info } }: Theme) =>
  createStyles({
    optionsContainer: {
      height: "100%",
      display: "flex",
      flexDirection: "row",
    },
    column: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
    },
    columnTitleText: {
      padding: spacing(1, 2),
      flexGrow: 0,
      flexShrink: 0,
    },
    row: {
      display: "flex",
      alignContent: "center",
      justifyContent: "space-between",
    },

    // Left Options Column
    mealTypeOptions: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      flexGrow: 1,
    },
    card: {
      marginBottom: spacing(3),
      marginLeft: spacing(2),
      marginRight: spacing(2),
      maxWidth: "700px",
    },
    iconColumn: {
      width: spacing(6),
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      height: "100%",
    },
    choRow: {
      display: "flex",
    },
    fixedExchange: {
      margin: spacing(),
    },

    // Right Additional Column
    additionalOptionsRoot: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      flexGrow: 1,
    },
    infoIcon: {
      color: info.main,
      fontSize: "1.5rem",
      paddingLeft: spacing(1),
    },
    additionalOptions: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "flex-start",
    },
    optionsGroup: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    },
  })
);

interface MealPlanCopyOptionsFormProps {
  toCopyMealPlan: CopyableMealPlanFragment;
  state: MealPlanCopyOptionsFormState;
  dispatch: Dispatch<MealPlanCopyOptionsFormAction>;
}

const MealPlanCopyOptionsForm = ({ state, dispatch, toCopyMealPlan }: MealPlanCopyOptionsFormProps) => {
  const classes = useStyles();
  const green = useTheme().palette.success.light;
  const exactCopyTitle = toCopyMealPlan.type === "exchange" ? "Exact Copy" : "Adjust Daily Targets + Copy Meal Targets";

  const exactCopyMsg =
    toCopyMealPlan.type === "exchange"
      ? `Exchanges will be copied exactly from the original meal plan`
      : `Each meal's macros will be copied exactly from the original meal plan, daily targets will adjust to the new athlete`;

  const autoAdjustMsg =
    toCopyMealPlan.type === "exchange"
      ? `Exchanges will be auto-adjusted using Nutrition's internal algorithm using anthropometry and goal`
      : `Both daily and meal macros will be auto-adjusted based on athlete anthropometry and goal`;

  return (
    <div className={classes.optionsContainer}>
      <div className={classes.mealTypeOptions}>
        <Typography
          variant="h4"
          className={classes.columnTitleText}
          color="textSecondary">
          {toCopyMealPlan.type === "exchange" ? "Exchanges" : "Macros"} Targets
        </Typography>

        <Card className={classes.card} variant="outlined">
          <CardActionArea onClick={() => dispatch({ type: "CHANGE_AUTOMATION_LEVEL", payload: "COPY" })}>
            <CardContent>
              <div className={classes.row}>
                <div className={classes.column}>
                  <Typography variant="h4">{exactCopyTitle}</Typography>
                  <Typography variant="caption">{exactCopyMsg}</Typography>
                </div>
                <div className={classes.iconColumn}>{state.automationStrategy === "COPY" && <CheckCircleIcon fontSize="large" />}</div>
              </div>
            </CardContent>
          </CardActionArea>
        </Card>

        {toCopyMealPlan.type === "exchange" && (
          <Card className={classes.card} variant="outlined">
            <CardActionArea
              onClick={() =>
                dispatch({
                  type: "CHANGE_AUTOMATION_LEVEL",
                  payload: "FIX_AND_ADJUST",
                })
              }
            >
              <CardContent>
                <div className={classes.row}>
                  <div className={classes.column}>
                    <Typography variant="h4">Fix and Adjust</Typography>
                    <Typography variant="caption">
                      Exchanges below will be fixed, others will adjust based on athlete's anthropometry and goal.
                    </Typography>
                    <div className={classes.choRow}>
                      <TextField
                        className={classes.fixedExchange}
                        margin="dense"
                        type="number"
                        disabled={state.automationStrategy !== "FIX_AND_ADJUST"}
                        label="Veg"
                        value={state.vegetableServingsInput}
                        onChange={e =>
                          dispatch({
                            type: "CHANGE_VEGETABLE_SERVINGS",
                            payload: e.target.value,
                          })
                        }
                      />
                      <TextField
                        className={classes.fixedExchange}
                        margin="dense"
                        type="number"
                        disabled={state.automationStrategy !== "FIX_AND_ADJUST"}
                        label="Fruit"
                        value={state.fruitServingsInput}
                        onChange={e =>
                          dispatch({
                            type: "CHANGE_FRUIT_SERVINGS",
                            payload: e.target.value,
                          })
                        }
                      />
                      <TextField
                        className={classes.fixedExchange}
                        margin="dense"
                        type="number"
                        disabled={state.automationStrategy !== "FIX_AND_ADJUST"}
                        label="Dairy"
                        value={state.dairyServingsInput}
                        onChange={e =>
                          dispatch({
                            type: "CHANGE_DAIRY_SERVINGS",
                            payload: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className={classes.iconColumn}>
                    {state.automationStrategy === "FIX_AND_ADJUST" && <CheckCircleIcon fontSize="large" style={{ color: green }} />}
                  </div>
                </div>
              </CardContent>
            </CardActionArea>
          </Card>
        )}

        <Card className={classes.card} variant="outlined">
          <CardActionArea onClick={() => dispatch({ type: "CHANGE_AUTOMATION_LEVEL", payload: "ADJUST" })}>
            <CardContent>
              <div className={classes.row}>
                <div className={classes.column}>
                  <Typography variant="h4">Auto Adjust All Targets</Typography>
                  <Typography variant="caption">{autoAdjustMsg}</Typography>
                </div>
                <div className={classes.iconColumn}>
                  {state.automationStrategy === "ADJUST" && <CheckCircleIcon fontSize="large" style={{ color: green }} />}
                </div>
              </div>
            </CardContent>
          </CardActionArea>
        </Card>
      </div>
      <div className={classes.additionalOptionsRoot}>
        <Typography
          variant="h4"
          className={classes.columnTitleText}
          color="textSecondary">
          Additional Options
        </Typography>
        <div className={classes.additionalOptions}>
          <div className={classes.optionsGroup}>
            <Checkbox
              checked={state.copyFoodPreferences}
              onChange={e =>
                dispatch({
                  type: "CHANGE_COPY_FOOD_PREFERENCES",
                  payload: e.target.checked,
                })
              }
            />
            <Typography className={classes.row}>
              Copy Promoted/Avoided Foods
              <Tooltip
                title={
                  <Typography variant="subtitle1">
                    If checked, promoted/avoided foods and food-groups will be copied to the new meal plans. This will effect exchange lists
                    on the new plan by auto-deselecting avoided foods.
                  </Typography>
                }
              >
                <InfoIcon fontSize="small" classes={{ root: classes.infoIcon }} />
              </Tooltip>
            </Typography>
          </div>
          {toCopyMealPlan.type === "exchange" && (
            <div className={classes.optionsGroup}>
              <Checkbox
                checked={state.copyPickListServingIds}
                onChange={e =>
                  dispatch({
                    type: "CHANGE_COPY_PICK_LIST_SERVING_IDS",
                    payload: e.target.checked,
                  })
                }
              />
              <Typography className={classes.row}>
                Copy Pick Lists
                <Tooltip
                  title={
                    <Typography variant="subtitle1">
                      If checked, all de-selected servings on the original meal plan's pick lists will also be de-selected in new meal
                      plans.
                    </Typography>
                  }
                >
                  <InfoIcon fontSize="small" classes={{ root: classes.infoIcon }} />
                </Tooltip>
              </Typography>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MealPlanCopyOptionsForm;
